/**
 * @file DownloadDropdown.tsx
 * @module DownloadDropdown
 * @description
 *   A dropdown component for downloading campaign assets and preview images.
 *   Uses Katal StyledDropdown component for consistent UI and accessibility.
 */

import { useCallback } from 'react';
import { AssetType } from '@amzn/fire-tv-campaign-automation-service';
import { KatDropdown } from '@amzn/katal-react';
import { useAssetContext } from 'src/hooks/useAssetContext';
import { AssetStatus, PreviewAssets } from 'src/types';
import { DownloadConstants } from 'src/constants';
import { downloadFromUrl, downloadZipFromUrls } from 'src/utils/downloadUtils';
import { StyledDropdown } from 'src/components/general/StyledDropdown';
import { logger } from 'src/logger';
import { useToast } from 'src/contexts/ToastContext';
import { PreviewConfigBase, PreviewState } from 'src/configs/previewConfigurations/types';
import { generatePreview } from 'src/utils/preview/PreviewGenerator';
import { buildAssetFilename, buildFilename, getTimestamp } from 'src/utils/fileUtils';
import styles from './DownloadDropdown.module.css';

/**
 * Props for the DownloadDropdown component
 * @interface DownloadDropdownProps
 * @property {PreviewAssets} assetUrls - URLs for downloading assets
 * @property {Record<string, string>} previewUrls - URLs for downloading preview images
 * @property {PreviewConfigBase} config - Preview configuration with download settings
 * @property {string} bodyCopy - Body copy text for preview generation
 */
interface DownloadDropdownProps {
  assetUrls: PreviewAssets;
  previewUrls: Record<string, string>;
  config: PreviewConfigBase;
  bodyCopy: string;
}

/**
 * DownloadDropdown component for downloading campaign assets and previews
 */
const DownloadDropdown = ({ assetUrls, previewUrls, config, bodyCopy }: DownloadDropdownProps) => {
  const { assetStates } = useAssetContext();
  const { addToast } = useToast();

  const getFileExtension = (type: AssetType) => DownloadConstants.ASSET_TYPE_EXTENSIONS[type];

  const getAllowedPreviewStates = useCallback((): PreviewState[] => {
    const allStates = Object.keys(config.stateConfigs) as PreviewState[];
    return config.downloads?.previewStates || allStates;
  }, [config]);

  const buildPreviewOptions = useCallback(() => {
    if (!config.downloads?.includePreviews) return [];

    return getAllowedPreviewStates().map((state) => ({
      name: `Preview: ${state}`,
      value: `preview_${state}`,
    }));
  }, [config, getAllowedPreviewStates]);

  const downloadAll = useCallback(async (): Promise<void> => {
    try {
      const timestamp = getTimestamp();
      const urlMap: Record<string, string> = {};

      // Add assets if configured
      if (config.downloads?.includeAssets) {
        Object.entries(assetStates)
          .filter(
            ([type, state]) =>
              state?.status === AssetStatus.COMPLETED && state?.key && assetUrls[type as AssetType],
          )
          .forEach(([type]) => {
            const assetType = type as AssetType;
            const filename = buildAssetFilename(assetType, getFileExtension(assetType), timestamp);
            urlMap[filename] = assetUrls[assetType]!;
          });
      }

      // Add previews if configured
      if (config.downloads?.includePreviews) {
        const allowedStates = getAllowedPreviewStates();
        const previewAssets = { ...assetUrls, bodyCopy };

        await Promise.all(
          allowedStates.map(async (state) => {
            let url = previewUrls[state];

            if (!url) {
              url = await generatePreview(previewAssets, state, config);
            }

            const filename = buildFilename(
              `${DownloadConstants.PREVIEW_FILE_PREFIX}_${state}`,
              `${DownloadConstants.PREVIEW_FILE_EXTENSION}`,
              timestamp,
            );
            urlMap[filename] = url;
          }),
        );
      }

      const zipFilename = buildFilename(DownloadConstants.ZIP_FILENAME_PREFIX, 'zip', timestamp);
      await downloadZipFromUrls(urlMap, zipFilename);
    } catch (error) {
      logger.error(DownloadConstants.DOWNLOAD_FAILED, error as Record<string, unknown>);
      addToast({
        description: DownloadConstants.DOWNLOAD_FAILED,
        variant: 'danger',
      });
    }
  }, [
    assetUrls,
    bodyCopy,
    assetStates,
    config.downloads,
    config,
    previewUrls,
    addToast,
    getAllowedPreviewStates,
  ]);

  const handleAssetDownload = useCallback(
    async (type: AssetType): Promise<void> => {
      const state = assetStates[type];
      const url = assetUrls[type];

      if (state?.status === AssetStatus.COMPLETED && state?.key && url) {
        try {
          const filename = buildAssetFilename(type, getFileExtension(type));
          await downloadFromUrl(url, filename);
        } catch (error) {
          logger.error(DownloadConstants.DOWNLOAD_FAILED, error as Record<string, unknown>);
          addToast({
            description: DownloadConstants.DOWNLOAD_FAILED,
            variant: 'danger',
          });
        }
      }
    },
    [assetUrls, assetStates, addToast],
  );

  const handlePreviewDownload = useCallback(
    async (state: PreviewState): Promise<void> => {
      try {
        let url = previewUrls[state];

        if (!url) {
          const previewAssets = { ...assetUrls, bodyCopy };
          url = await generatePreview(previewAssets, state, config);
        }

        const filename = buildFilename(`${DownloadConstants.PREVIEW_FILE_PREFIX}_${state}`, 'png');
        await downloadFromUrl(url, filename);
      } catch (error) {
        logger.error(DownloadConstants.DOWNLOAD_FAILED, error as Record<string, unknown>);
        addToast({
          description: `Failed to generate/download preview: ${state}`,
          variant: 'danger',
        });
      }
    },
    [previewUrls, assetUrls, bodyCopy, config, addToast],
  );

  const hasCompletedAssets =
    config.downloads?.includeAssets &&
    Object.values(assetStates).some((s) => s?.status === AssetStatus.COMPLETED && s?.key);

  const hasPreviews = config.downloads?.includePreviews && Object.keys(previewUrls).length > 0;

  const options = [
    // Asset options
    ...(config.downloads?.includeAssets
      ? (
          config.downloads.supportedAssets || [
            AssetType.BACKGROUND_IMAGE,
            AssetType.LOGO,
            AssetType.VIDEO_TRAILER,
          ]
        )
          .filter((assetType) => assetUrls[assetType])
          .map((assetType) => {
            const state = assetStates[assetType];
            return {
              name: DownloadConstants.ASSET_TYPE_LABELS[assetType],
              value: assetType,
              disabled: !state || state.status !== AssetStatus.COMPLETED || !state.key,
            };
          })
      : []),
    // Preview options
    ...buildPreviewOptions(),
    // Download All
    {
      name: DownloadConstants.LABELS.DOWNLOAD_ALL,
      value: DownloadConstants.DOWNLOAD_ALL_VALUE,
      disabled: !hasCompletedAssets && !hasPreviews,
    },
  ];

  const handleChange = useCallback(
    (event: KatDropdown.ChangeEvent) => {
      const value = event.detail.value;

      const dropdown = event.target as HTMLSelectElement | null;
      if (dropdown) {
        dropdown.value = '';
      }

      if (value === DownloadConstants.DOWNLOAD_ALL_VALUE) {
        downloadAll();
      } else if (value.startsWith('preview_')) {
        const state = value.replace('preview_', '') as PreviewState;
        handlePreviewDownload(state);
      } else {
        handleAssetDownload(value as AssetType);
      }
    },
    [downloadAll, handleAssetDownload, handlePreviewDownload],
  );

  return (
    <StyledDropdown
      placeholder={DownloadConstants.LABELS.DOWNLOAD_ASSETS}
      options={options}
      onChange={handleChange}
      data-testid="download-assets-dropdown"
      className={styles.dropdown}
    />
  );
};

DownloadDropdown.displayName = 'DownloadDropdown';

export { DownloadDropdown };
